import toast from "react-hot-toast";
import { getSecurityErrorMessage } from "@/lib/securityErrors";
import { clearUserState } from "@/lib/logout";
import { ApiBaseUrl } from "@/redux/http";
import {
  Restaurant,
  RestaurantFranchise,
} from "@/redux/services/restaurant/restaurant.type";
import { Roles, User } from "@/redux/services/user/user.type";
import {
  BaseQueryFn,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError
} from "@reduxjs/toolkit/query/react";

type ScopeState = {
  user?: {
    currentUser?: User;
    currentRestaurant?: Restaurant;
    currentFranchise?: RestaurantFranchise;
  };
};

/**
 * Restaurant and franchise scoping headers.
 *
 * - X-Restaurant-Id (+ JSON-LD Accept) when a restaurant is selected.
 * - X-Franchise-Id for owners with a selected franchise (SFRESTO-169): the API
 *   only grants an owner access to the restaurants of that franchise and lists
 *   only its restaurants (FranchiseService::getCurrentFranchise()). Never sent
 *   for other roles: the API ignores a franchise header from a non-owner by
 *   returning no franchise at all.
 */
export const prepareScopeHeaders = (headers: Headers, state: ScopeState) => {
  const restaurantId = state.user?.currentRestaurant?.id;
  if (restaurantId) {
    headers.set("X-Restaurant-Id", restaurantId);
    headers.set("Accept", "application/ld+json");
  }

  const franchiseId = state.user?.currentFranchise?.id;
  const isOwner = state.user?.currentUser?.roles?.includes(Roles.owner) ?? false;
  if (isOwner && franchiseId) {
    headers.set("X-Franchise-Id", franchiseId);
  }

  return headers;
};

const baseQuery = fetchBaseQuery({
  baseUrl: `${ApiBaseUrl}/api/`,
  credentials: "include",
  prepareHeaders: (headers, { getState }) =>
    prepareScopeHeaders(headers, getState() as ScopeState),
})

/**
 * A 401, or a 404 on `users/current`: the API answers an anonymous session
 * there with 404 (no current user), not 401, so without this the logout flow
 * never ran for that call (SFRESTO-167).
 */
export const isSessionLost = (
  args: string | FetchArgs,
  status: FetchBaseQueryError["status"]
): boolean => {
  if (status === 401) return true;
  const url = (typeof args === "string" ? args : args.url).replace(/^\/+/, "");
  return status === 404 && url === "users/current";
};

export const baseQueryWithLogout: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (args, api, extraOptions) => {
  const result = await baseQuery(args, api, extraOptions);
  if (result.error && isSessionLost(args, result.error.status)) {
    // A session the API ended (inactive account, no workplace left) carries
    // the reason in `message` (SFAPI-180); tell the user why they are logged out.
    const data = result.error.data as { message?: string } | undefined;
    const reason = getSecurityErrorMessage(data?.message);
    if (reason) {
      toast.error(reason, { id: "session-revoked" });
    }
    // Same cleanup as a manual logout (franchise, POS cart, API caches),
    // once: later 401s of the same burst find the session already cleared
    // (SFRESTO-164).
    const state = api.getState() as { auth?: { isAuthenticated?: boolean } };
    if (state.auth?.isAuthenticated !== false) {
      clearUserState(api.dispatch);
    }
  }

  return result;
}
